import {inject, Injectable, DOCUMENT, signal} from '@angular/core';
import {Router} from "@angular/router";
import {AppInfoService} from "../../../core/services/app-info.service";
import {clearInterval, setInterval} from "worker-timers";
import {ClsCliente} from 'src/app/core/models/cls-cliente';
import {KeyLocalStorage, ProveedorLogin} from 'src/app/core/models/enum/emun-enumeracion';
import {ClsUtil} from 'src/app/util/cls-util';
import {ClsConfig} from "../../../util/cls-config";
import {ClsObjGenerico} from "../../../clases-pu/util/cls-obj-generico";
import {ConexionRedService} from "../../../core/services/conexion-red.service";
import {TipoMensajesService} from "../../../core/services/tipo-mensajes.service";
import { environment } from 'src/environments/environment';
import {ClsCode} from "../../../core/models/cls-access-token";

@Injectable({
  providedIn: 'root'
})
export class AuthLoginService extends ClsConfig {
  private conexionRedService = inject(ConexionRedService);
  private appInfAppSer = inject(AppInfoService);
  private document = inject(DOCUMENT);
  private router = inject(Router);
  private tipoMsg = inject(TipoMensajesService);

  public mObjGenerico: ClsObjGenerico = new ClsObjGenerico();
  public readonly loggedIn = signal<boolean>(false);
  protected readonly timeLeft = signal<number>(2400);  // 40min
  // public loggedIn = false;
  // private timeLeft: number = 2400;
  private interval: any;
  public URL_GOOGLE: string = 'https://accounts.google.com/o/oauth2/v2/auth?client_id=' + environment.googleClientId +
    '&response_type=code&access_type=offline&' +
    'redirect_uri={proyRedirect}&' +
    'scope=https://www.googleapis.com/auth/userinfo.email%20' +
    // 'https://mail.google.com/%20' +
    'https://www.googleapis.com/auth/gmail.send%20' +
    'https://www.googleapis.com/auth/userinfo.profile%20';
  public URL_MICROSOFT: string = 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize?' +
    'client_id=' + environment.microsoftClientId + '&response_type=code' +
    '&redirect_uri={proyRedirect}&response_mode=query' +
    '&scope=openid%20offline_access%20Mail.Send%20User.Read%20User.ReadBasic.All';
  public URL_LOGOUT_GOOGLE = 'https://www.google.com/accounts/Logout?continue=https://appengine.google.com/_ah/logout?continue=';
  public URL_LOGOUT_MICROSOFT = 'https://login.microsoftonline.com/common/oauth2/v2.0/logout?post_logout_redirect_uri=';

  constructor() {
    super();
  }

  /**
   * este método nos permite iniciar sesión con Google o Microsoft
   * @param proveedorLogin
   */
  public login(proveedorLogin: string) {
    const REDIRECT_URL = `${this.gUrlProyecto}/code`;
    switch (proveedorLogin) {
      case ProveedorLogin.Google:
        this.document.location.href = this.URL_GOOGLE.replace('{proyRedirect}', REDIRECT_URL);
        break;
      case ProveedorLogin.Microsoft:
        this.document.location.href = this.URL_MICROSOFT.replace('{proyRedirect}', REDIRECT_URL);
        break;
      default:
        this.accesoDenegado();
        break;
    }
  }

  /**
   * Método que se ocupa para cerrar sesión Google o cerrar sesión del sistema
   * @param redirecciona
   */
  public async logOut(redirecciona = true) {
    try {
      await this.mObjGenerico.peticionServidorLogin(ClsUtil.obtenerAccessToken(this.appInfAppSer), 'AuthRevokeSesion');
      if (!redirecciona) {
        this.accesoDenegado();
      }

      // utilizando el operador || para asignarle un valor por defecto (ProveedorLogin.Google)
      // en caso de que el valor obtenido del localStorage sea null o undefined.
      const prov: any = localStorage.getItem(KeyLocalStorage.ProvLogin);
      const REDIRECT_URL = `${this.gUrlProyecto}/login-form`;
      this.document.location.href = (prov === ProveedorLogin.Google) ? `${this.URL_LOGOUT_GOOGLE}${REDIRECT_URL}` : `${this.URL_LOGOUT_MICROSOFT}${REDIRECT_URL}`;
      this.restaurarValoresDefaultsLogin();
    } catch (e) {
      await new Promise(resolve => this.tipoMsg.mostrarMsgPromise((resp) => resolve(resp), this.mObjGenerico.getTranslationsAlertReinicar(e.message))).then(value => value);
      this.accesoDenegado();
    }
  }

  /**
   * Método que se ocupa cuando pasa todas las validaciones de logueo
   */
  public async accesoExitoso() {
    this.loggedIn.set(true);
    try {
      this.startTimer();
      // Inicia chequeo periódico de velocidad
      this.conexionRedService.startSpeedCheck().catch(async (e) => {
        await new Promise(resolve => this.tipoMsg.mostrarMsgPromise((resp) => resolve(resp), this.mObjGenerico.getTranslationsAlertReinicar(e.message))).then(value => value);
        this.accesoDenegado();
      });
      await this.router.navigate(['/principal']);
    } catch (e) {
      this.loggedIn.set(false);
      this.accesoDenegado();
    }
  }

  /**
   * Método que se ocupa para actualizar el token cada 40 min
   * @private
   */
  private startTimer() {
    const intervalDuration = 1000; // Intervalo de 1 segundo
    const refreshTime = 2400; // Tiempo de refresco en segundos (40 minutos)
    this.interval = setInterval(async () => {
      if (this.timeLeft() > 0) {
        this.timeLeft.update(v => v - 1);
        // console.log(this.timeLeft);
      } else {
        try {
          const resp: ClsCliente = await this.confirmarSesionLogin();
          this.timeLeft.set(refreshTime);
          // console.log('Refrescando token');
          clearInterval(this.interval);
          this.inicializarDatosLogin(resp);
          this.startTimer();
        } catch (e) {
          // await new Promise(resolve => this.tipoMsg.mostrarMsgPromise((resp) => resolve(resp), this.mObjGenerico.getTranslationsAlertReinicar(e.message))).then(value => value);
          this.tipoMsg.mostrarMsg(this.mObjGenerico.getTranslationsAlertReinicar(e.message));
          this.accesoDenegado();
        }
      }
    }, intervalDuration);
  }

  /**
   * Método que se encarga de obtener la sesión del usuario
   */
  public async obtieneSesionLogin(): Promise<ClsCliente> {
    try {
      const servicio: string = this.appInfAppSer.provLogin === ProveedorLogin.Google ? 'AuthObtieneSesion' : 'AuthObtieneSesionMicrosoft';
      return await this.mObjGenerico.peticionServidorLogin(ClsUtil.obtenerAccessToken(this.appInfAppSer), servicio);
    } catch (e: any) {
      throw new Error(e.message);
    }
  }

  /**
   * Método que se encarga de confirmar la sesión actual del usuario, en caso de tener una nueva sesión en otro sistema
   * le mandara mensaje de que inicio sesion en otro sistema y sera dirigido al login
   */
  public async confirmarSesionLogin(): Promise<ClsCliente> {
    try {
      const servicio: string = this.appInfAppSer.provLogin === ProveedorLogin.Google ? 'AuthConfirmarSesion' : 'AuthConfirmarSesionMicrosoft';
      return await this.mObjGenerico.peticionServidorLogin(ClsUtil.obtenerAccessToken(this.appInfAppSer), servicio);
    } catch (e: any) {
      throw new Error(e.message);
    }
  }

  /**
   * Método que se ocupa para redireccionar al Login porque alguna petición
   * de confirma sesión, revocar token, obtener token, obtener sesion, 400 Bad Request,
   * invalid_grant salio mal
   */
  public accesoDenegado() {
    this.restaurarValoresDefaultsLogin();
    this.router.navigate(['/login-form']).then(r => {
      window.location.reload();
    });
  }

  /**
   * Método que se ocupa para inicializar los nuevos datos en base el Login para
   * ocuparse en todo el sistema
   * @param resp
   */
  public inicializarDatosLogin(resp: ClsCliente) {
    const date = new Date().toISOString().substring(0, 10).replace(/-/g, '');
    const hour = new Date();
    // Determinar si el usuario es demo
    this.appInfAppSer.esUsuarioDemo = (resp.noTieneVigencia || resp.cambioIp || resp.caducoSesion || resp.tokenCaducado || resp.Offline);
    // Modificar la imagen según el proveedor de login
    if (this.appInfAppSer.provLogin === ProveedorLogin.Microsoft) {
      resp.picture = 'data:image/png;base64,' + resp.picture;
    }
    // Construir el logTime
    const minutes = (hour.getMinutes() < 10 ? '0' : '').toString() + hour.getMinutes().toString();
    resp.logTime = `${date}${hour.getHours().toString()}${minutes}`;
    // Establecer el modo de login
    resp.modoLogin = this.appInfAppSer.provLogin;
    // Establecer esAdmin, ya que cuendo hace refresh o confirma sesion no trae bien el dato resp.admin
    resp.admin = resp.email === resp.emailAdmin;
    // Guardar la sesión de login
    this.guardarSesionLogin(resp);
    // Establecer el usuario logeado en el servicio
    this.appInfAppSer.usuarioLogeado = resp;
  }

  /**
   * Método que se ocupa para limpiar información de la sesión y proveedor con el que estaba
   * logueado el usuario y por alguna peticion que salio mal se necesita inicialiar los valores
   * para poder iniciar sesion desde la ventana Login
   * @private
   */
  private restaurarValoresDefaultsLogin() {
    this.loggedIn.set(false);
    this.appInfAppSer.usuarioLogeado = new ClsCliente();
    localStorage.removeItem(KeyLocalStorage.Sesion);
    localStorage.removeItem(KeyLocalStorage.ProvLogin);
    clearInterval(this.interval);
    this.conexionRedService.resetSpeedCheck();
  }

  /**
   * Método que se ocupa para guardar la sesión por usuario(email)
   * @param resp
   */
  public guardarSesionLogin(resp: ClsCliente) {
    const sesiones: any = ClsUtil.parseJSONBig(localStorage.getItem(KeyLocalStorage.Sesion)) || [];

    const existingSesionIndex = sesiones.findIndex(sesion => sesion.email === resp.email);

    if (existingSesionIndex !== -1) {
      // Actualizar la sesión existente
      sesiones[existingSesionIndex].sesion = resp.sesion;
    } else {
      // Agregar una nueva sesión
      sesiones.push({email: resp.email, sesion: resp.sesion});
    }
    // Guardar en localStorage
    localStorage.setItem(KeyLocalStorage.Sesion, ClsUtil.stringifyJSONBig(sesiones));
  }

  public async procesarSesion(resp: ClsCliente): Promise<ClsCliente> {
    const sesiones: any[] = ClsUtil.parseJSONBig(localStorage.getItem(KeyLocalStorage.Sesion)) || [];

    const currentSesion = sesiones.find(s => s.email === resp.email);
    resp.sesion = currentSesion?.sesion ?? 0;

    this.appInfAppSer.usuarioLogeado = resp;

    return (!ClsUtil.esNulo(currentSesion) && resp.sesion > BigInt(0))
      ? this.confirmarSesionLogin()
      : this.obtieneSesionLogin();
  }


  public construirCode(codeParam: string): ClsCode {
    const code = new ClsCode();
    code.Code = codeParam;
    code.CodigoSistema = 'PU';
    // code.EsNube = true;
    code.RedirectUrl = `${this.gUrlProyecto}/code`;
    // code.Proveedor = this.appInfAppSer.provLogin;
    return code;
  }
}
